/**
 * TTS Flow — text-to-speech automation builder. Templates 7/8/9 cover
 * Simple TTS / TTS+DTMF / TTS+Call Patch respectively. The backend wire
 * format is several nested JSON-stringified payloads — see payload.ts
 * for the build/hydrate functions.
 */

import { Check, Pencil, Plus, Trash2, X, type LucideIcon } from "lucide-react";

/** Template ids the legacy backend understands. Must remain string-typed. */
export type TtsTemplateId = '7' | '8' | '9';

export interface TtsTemplateOption {
  value: TtsTemplateId;
  label: string;
  description: string;
}

export const TTS_TEMPLATES: TtsTemplateOption[] = [
  {
    value: '7',
    label: 'TTS Simple',
    description: 'Play a TTS prompt — no DTMF, no patch.',
  },
  {
    value: '8',
    label: 'TTS + DTMF',
    description: 'Play a TTS prompt, accept a DTMF key from the caller, branch.',
  },
  {
    value: '9',
    label: 'TTS + Call Patch',
    description: 'Play a TTS prompt, accept DTMF, transfer to an agent group on success.',
  },
];

/**
 * Wire-format row type. Only `prompt` and `variable` make it to the
 * backend — see auto-synthesize logic in payload.ts for how editor-only
 * `text` rows are promoted into `prompt` rows at save time.
 */
export type TtsRowType = 'prompt' | 'variable';

/**
 * Editor-only mode used by the two-pane builder. `text` rows do NOT exist
 * on the wire — they're auto-synthesized to a prompt before submit.
 */
export type TtsEditorMode = 'prompt' | 'text' | 'variable';

export interface TtsRow {
  ttsType: TtsRowType;
  /** Prompt → promptId (string). Variable → slot number (1–10). */
  value: string;
  /** Variable rows only — the tts_type string (e.g. "number", "text"). */
  variableType: string;
  /**
   * Editor-only: when set, this row was authored as inline text and will
   * be synthesized into a prompt at save time. Wire-time payload.ts
   * strips this field so the JSON the backend sees stays exactly as it
   * was in the legacy app.
   */
  text?: string;
  /**
   * Editor-only: optional cached preview URL after the user clicks the
   * ▶ preview on a `text` row. Lets us play it back without re-spending
   * credits on /convertion/texttospeech. Stripped at save time.
   */
  previewUrl?: string;
  /**
   * Editor-only: when a text row has been previewed, we hold the ttsId
   * returned by /convertion/texttospeech so the save step can finalize
   * that pending row instead of re-synthesizing from scratch.
   */
  previewTtsId?: number;
}

/** True when the editor row should be rendered as a text-mode card. */
export function isEditorTextRow(r: TtsRow): boolean {
  return typeof r.text === 'string' && r.text.length > 0 && r.ttsType !== 'variable';
}

export type Gender = 'MALE' | 'FEMALE';

export interface TtsPatchRow {
  groupId: string;
  groupName: string;
  agentDtmf: string;
}

/* ---------------- List row ---------------- */

/* ---------------- TTS Logs (continuous logs) ---------------- */

/** Row from POST /api/obd/get/continuous/logs. */
export interface TtsLogRow {
  id: number;
  campaignId?: number | string;
  uniqueKey?: string;
  requestDate?: string;
  successCount?: number;
  failedCount?: number;
  dndCount?: number;
  totalNos?: number;
  /** JSON-serialized payload string the runner submitted. */
  requestPayload?: string;
}

export interface TtsLogRequest {
  userId: number | string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface TtsFlowListRow {
  id: number;
  ttsFlowName: string;
  templateId: string;
  status: string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  userId: number;
  campaignIds: string;
}

export interface AdminTtsFlowDetail extends TtsFlowListRow {
  /** Serialized TtsFlowFormValues (tts_flow_json column). */
  ttsFlow: string;
}

export type TtsFlowReviewAction = 'approve' | 'reject';

export interface BulkDeleteTtsFlowsResponse {
  message?: string;
  requested: number;
  deleted: number;
  notFound: number;
}

export interface BulkReviewTtsFlowsResponse {
  message?: string;
  requested: number;
  updated: number;
  alreadyApproved: number;
  alreadyRejected: number;
  skipped: number;
}

/* ---------------- GET-detail wire shape ---------------- */

/** Raw response from GET /api/obd/get/tts/flow/{id} — all the nested JSON
 *  blobs come back as strings and need parsing. */
export interface TtsFlowDetailResponse {
  id: number;
  ttsFlowName: string;
  templateId: string;
  /** JSON string of `{ language, gender, ttsRows: { ttsList: [...] } }`. */
  ttsFlow: string;
  /** JSON string of menu/dtmf/voice-file ids + wait/re-prompt. */
  templateData: string;
  /** JSON string of SMS triggers + webhook config. */
  smsData: string;
  /** JSON string of `{ patchList: [...] }` or empty string. */
  agentRows: string;
  ivrId?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  campaignIds?: string;
}

export type TtsFlowLogAction = 'create' | 'update' | 'delete' | 'approve' | 'rejected';

export interface TtsFlowLogRow {
  id: number;
  ttsFlowId: number;
  createdAt: string;
  action: TtsFlowLogAction;
  userId: number | null;
  // mysql2 returns JSON columns as parsed objects; some drivers return strings.
  oldTtsFlowJson: unknown;
  newTtsFlowJson: unknown;
}

/* ---------------- Compose/update request (wire format) ---------------- */

/**
 * POST /api/obd/add/tts/flow body. Identical to update except `id`. The
 * three big nested objects are stringified before being attached.
 */
export interface TtsFlowComposeRequest {
  userId: number | string;
  templateId: TtsTemplateId | '';
  ttsFlowName: string;
  /** JSON-stringified `{ language, gender, ttsRows: { ttsList: [...] } }`. */
  ttsFlow: string;
  /** JSON-stringified `{ dtmf, welcomePId, menuPId, noInputPId, ... }`. */
}

export interface TtsFlowUpdateRequest extends TtsFlowComposeRequest {
  id: number;
}

/* ---------------- Form values (UI state) ---------------- */

/**
 * In-form state for the wizard. The wire-format conversion lives in
 * payload.ts (buildTtsFlowRequest / hydrateTtsFlowFromDetail).
 */
export interface TtsFlowFormValues {
  /** Pre-creation: '' means template not chosen yet. */
  templateId: TtsTemplateId | '';
  ttsFlowName: string;

  /** Language code from /api/obd/user/languages, e.g. "en-IN". */
  language: string;
  gender: Gender | '';

  /** TTS rows the user has built (prompt OR variable). */
  ttsRows: TtsRow[];

  /** Agent-group patch rows (used only by the legacy StepPatch editor). */
  patchList: TtsPatchRow[];
}

export const DEFAULT_TTS_FORM_VALUES: TtsFlowFormValues = {
  templateId: '',
  ttsFlowName: '',
  language: '',
  gender: '',
  ttsRows: [],
  patchList: [],
};

/* ---------------- Template-feature flags ---------------- */

export interface TtsTemplateFeatures {
  /** Template 8 + 9 — collect DTMF input. */
  hasDtmf: boolean;
  /** Template 9 — patch list to agent groups. */
  hasPatch: boolean;
  /** Template 8 + 9 — menu prompt + wait + re-prompt + optional aux files. */
  hasVoiceFiles: boolean;
  /** Template 8 + 9 — SMS on DTMF trigger available. */
  hasSmsDtmf: boolean;
  /** Template 9 — patch SMS triggers. */
  hasPatchSms: boolean;
  /** Template 9 — requires the no-agent voice file. */
  needsNoAgent: boolean;
}

export function templateFeatures(t: TtsTemplateId | ''): TtsTemplateFeatures {
  const dtmf = t === '8' || t === '9';
  const patch = t === '9';
  return {
    hasDtmf: dtmf,
    hasPatch: patch,
    hasVoiceFiles: dtmf,
    hasSmsDtmf: dtmf,
    hasPatchSms: patch,
    needsNoAgent: patch,
  };
}

export function templateLabel(t: string | TtsTemplateId | ''): string {
  return TTS_TEMPLATES.find((opt) => opt.value === t)?.label ?? String(t || '');
}

export const REVIEW_STATUS: Record<string, { label: string; className: string }> = {
  '0': { label: 'Processing', className: 'bg-amber-100 text-amber-900 hover:bg-amber-100' },
  '1': { label: 'Approved', className: 'bg-emerald-100 text-emerald-900 hover:bg-emerald-100' },
  '2': { label: 'Rejected', className: 'bg-red-100 text-red-900 hover:bg-red-100' },
  '3': { label: 'Deleted', className: 'bg-zinc-100 text-zinc-600 hover:bg-zinc-100' },
};

export const ACTION_META: Record<
  TtsFlowLogAction,
  { label: string; icon: LucideIcon; className: string }
> = {
  create: {
    label: 'Created',
    icon: Plus,
    className: 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  },
  update: {
    label: 'Updated',
    icon: Pencil,
    className: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  delete: {
    label: 'Deleted',
    icon: Trash2,
    className: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  },
  approve: {
    label: 'Approved',
    icon: Check,
    className: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  rejected: {
    label: 'Rejected',
    icon: X,
    className: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  },
};